import React, { useEffect, useRef, useState } from "react";
import axiosClient from "../../api/axiosClient";

export default function PricingTimeZonePanel({ businessId, canManage, revision = 0, onStatusChange }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const mounted = useRef(true);
  const requestVersion = useRef(0);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const version = ++requestVersion.current;
    const controller = new AbortController();
    setLoading(true);
    setBusy(false);
    setError("");
    setMessage("");
    onStatusChange?.(null);
    axiosClient.get("/whatsappsettings/me", {
      signal: controller.signal, params: { businessId }, __silent: true,
    }).then(response => {
      if (!active || version !== requestVersion.current) return;
      const body = response?.data;
      const data = body?.hasSettings === false ? null : body?.data ?? body;
      setSettings(data || null);
      onStatusChange?.(data?.wabaPricingTimeZoneStatus || "Missing");
    }).catch(() => {
      if (active) { setError("Billing timezone could not be loaded. Reload this page to retry."); setSettings(null); }
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [businessId, revision, onStatusChange]);

  const refresh = async () => {
    if (busy) return;
    const version = requestVersion.current;
    setBusy(true); setError(""); setMessage("");
    try {
      const response = await axiosClient.post("/whatsappsettings/pricing-timezone/refresh", {}, {
        params: { businessId }, __silent: true,
      });
      if (!mounted.current || version !== requestVersion.current) return;
      const result = response.data;
      if (!result.success) throw new Error(result.message || "Billing timezone verification failed.");
      setSettings(current => ({ ...current, wabaPricingTimeZoneId: result.timeZoneId, wabaPricingTimeZoneStatus: result.status }));
      onStatusChange?.(result.status);
      setMessage(result.message || "Billing timezone verified with Meta.");
    } catch (failure) {
      if (mounted.current && version === requestVersion.current) setError(failure?.response?.data?.message || failure.message || "Billing timezone could not be refreshed. Try again.");
    } finally { if (mounted.current && version === requestVersion.current) setBusy(false); }
  };

  if (loading) return <p className="text-sm text-slate-500" aria-live="polite">Loading WABA billing timezone...</p>;
  if (!settings && !error) return null;
  const status = settings?.wabaPricingTimeZoneStatus || "Missing";
  return <section aria-label="WABA billing timezone" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">WABA billing timezone</h2>
        <p className="mt-1 text-sm text-slate-700">{settings?.wabaPricingTimeZoneId || "Not configured"}</p>
        <p className="mt-1 text-xs text-slate-500">Status: {status}. Used to calculate the monthly service-message allowance.</p>
      </div>
      {canManage && <button type="button" onClick={refresh} disabled={busy || !settings}
        className="rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? "Verifying..." : "Refresh billing timezone from Meta"}
      </button>}
    </div>
    {settings && status !== "Configured" && <p className="mt-3 text-sm text-amber-800">
      WhatsApp billing setup is incomplete. {canManage ? "Refresh the billing timezone from Meta to complete this step." : "Ask an account administrator to refresh the billing timezone from Meta."}
    </p>}
    {message && <p role="status" className="mt-3 text-sm text-emerald-800">{message}</p>}
    {error && <p role="alert" className="mt-3 text-sm text-rose-700">{error}</p>}
  </section>;
}
